import { env } from "env";
import { useRef, useState } from "react";
import { getLocalStorageAccessToken, getStoredUser } from "utils/helpers/localStorage";

export function useGridUpload({
  uploadEndpoint,
  envLang,
  tableTitle,
}: {
  uploadEndpoint: string | null;
  envLang: { lang: string; envNumeric: number | undefined };
  tableTitle: string;
}) {
  const uploadInputRef = useRef<HTMLElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUploadClick = () => {
    if (isUploading) return;
    (uploadInputRef.current as HTMLInputElement | null)?.click();
  };

  const handleUploadFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isUploading) return;
    const file = event.target.files?.[0];
    if (!file || !uploadEndpoint) return;

    setIsUploading(true);
    try {
      const token = getLocalStorageAccessToken();
      const authHeaders = token ? { authorization: `Bearer ${token}` } : undefined;
      const storedUser = getStoredUser<{ id?: number | string }>();

      const formData = new FormData();
      if (storedUser?.id != null) {
        formData.append("userId", String(storedUser.id));
      }
      formData.append("name", file.name);
      formData.append("file", file);

      const uploadRes = await fetch(`${env.api_url}/file`, {
        method: "POST",
        headers: authHeaders,
        body: formData,
      });

      if (!uploadRes.ok) throw new Error(`Upload failed: ${uploadRes.status}`);
      const uploadedFile = await uploadRes.json();
      if (!uploadedFile) throw new Error("Missing uploaded file payload");

      const payload: Record<string, unknown> = { file: uploadedFile };
      if (tableTitle !== "Translations") {
        payload.env = envLang.envNumeric;
        payload.lang = envLang.lang;
      }

      const saveRes = await fetch(`${env.api_url}${uploadEndpoint}`, {
        method: "POST",
        headers: authHeaders
          ? { "Content-Type": "application/json", ...authHeaders }
          : { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!saveRes.ok) throw new Error(`Import failed: ${saveRes.status}`);
      globalThis.location.reload();
    } catch (error) {
      console.error("Mass upload error", error);
    } finally {
      setIsUploading(false);
      event.target.value = "";
    }
  };

  return { uploadInputRef, handleUploadClick, handleUploadFileChange, isUploading };
}
